import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Send, Mic, Square, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Shell, TopBar } from "../components/Shell";
import { Textarea } from "../components/ui/textarea";
import { COMMON_QUESTIONS, DISCLAIMER } from "../lib/data";
import { api, getSessionId } from "../lib/api";

export default function Chat() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sid = params.get("sid");
  const q = params.get("q");
  const sessionId = getSessionId();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const scrollRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const autoSent = useRef(false);

  useEffect(() => {
    const url = sid ? `/screenings/${sid}/chat` : `/chat/general/${sessionId}`;
    api.get(url).then((r) => {
      setMessages(r.data.messages || []);
      if (q && !autoSent.current) { autoSent.current = true; send(q); }
    }).catch(() => {
      if (q && !autoSent.current) { autoSent.current = true; send(q); }
    });
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (t) => {
    const msg = (t ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setSending(true);
    try {
      let reply;
      if (sid) {
        const r = await api.post(`/screenings/${sid}/chat`, { message: msg });
        reply = r.data.reply;
      } else {
        const r = await api.post(`/chat/general`, { session_id: sessionId, message: msg });
        reply = r.data.reply;
      }
      setMessages((m) => [...m, { role: "assistant", text: reply }]);
    } catch (e) {
      toast.error("DentaFriend is unavailable right now.");
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I couldn't respond just now. Please try again." }]);
    } finally {
      setSending(false);
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", new Blob(chunksRef.current, { type: "audio/webm" }), "q.webm");
          const r = await api.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (r.data.text) setInput((p) => (p ? p + " " : "") + r.data.text);
          else toast.message(r.data.error || "Didn't catch that.");
        } catch (e) { toast.error("Couldn't transcribe."); }
        finally { setTranscribing(false); }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) { toast.error("Microphone access denied."); }
  };
  const stopRec = () => { if (mediaRef.current?.state !== "inactive") mediaRef.current?.stop(); setRecording(false); };

  return (
    <Shell header={<TopBar title="Ask DentaFriend" onBack={() => navigate(sid ? `/report?sid=${sid}` : "/")} />}>
      <div className="px-6 pt-4">
        <div className="flex gap-2.5 rounded-2xl brand-gradient-soft px-4 py-3">
          <MessageCircle className="w-5 h-5 text-[#8FE3BE] shrink-0 mt-0.5" />
          <p className="text-[12.5px] leading-relaxed text-white/90">
            Hi, I'm DentaFriend. {DISCLAIMER}
          </p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar px-6 py-4 flex flex-col gap-3">
        {messages.length === 0 && !sending && (
          <div className="flex flex-col gap-2 mt-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Common questions</p>
            {COMMON_QUESTIONS.map((c) => (
              <button key={c.label} data-testid="chat-suggestion" onClick={() => send(c.q)}
                className="tap text-left text-sm rounded-2xl border border-border bg-card px-4 py-3">{c.q}</button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`chat-msg-${m.role}`}
            className={`max-w-[86%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl whitespace-pre-wrap ${m.role === "user" ? "self-end bg-primary text-primary-foreground rounded-br-md" : "self-start bg-card border border-border rounded-bl-md"}`}>
            {m.text}
          </div>
        ))}
        {sending && (
          <div className="self-start bg-card border border-border rounded-2xl rounded-bl-md px-4 py-3 flex gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-soft" />
            <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-soft" style={{ animationDelay: ".2s" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-soft" style={{ animationDelay: ".4s" }} />
          </div>
        )}
      </div>

      <div className="sticky bottom-0 px-4 py-3 bg-background border-t border-border">
        <div className="flex items-end gap-2">
          <button data-testid="chat-voice" onClick={recording ? stopRec : startRec} disabled={transcribing}
            className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center tap ${recording ? "bg-destructive text-white pulse-soft" : "bg-accent text-primary"}`}>
            {transcribing ? <Loader2 className="w-5 h-5 animate-spin" /> : recording ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
          <Textarea data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} rows={1}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask about dental health…" className="min-h-11 max-h-32 rounded-2xl resize-none py-2.5" />
          <button data-testid="chat-send" onClick={() => send()} disabled={sending || !input.trim()}
            className="shrink-0 w-11 h-11 rounded-full brand-gradient text-white flex items-center justify-center tap disabled:opacity-40">
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </Shell>
  );
}
